import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import Magnetic from '../components/ui/Magnetic';
import { getOptimizedImageUrl } from '../lib/utils';
import { ProductCard } from '../components/ProductCard';
import { productsAPI, seedAPI, statsAPI } from '../lib/api';

const TSPM_API = "https://tspm-api.abrandfantastic.workers.dev";

// Lazy load RotatingTagline to move gsap out of the main/page bundle
const RotatingTagline = lazy(() => import('../components/RotatingTagline'));

function HeroSlider({ slides }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (slides.length <= 1 || paused) return;
    const timer = setInterval(() => setActive(i => (i + 1) % slides.length), 4000);
    return () => clearInterval(timer);
  }, [slides.length, paused]);

  const prev = () => setActive(i => (i - 1 + slides.length) % slides.length);
  const next = () => setActive(i => (i + 1) % slides.length);

  return (
    <div
      className="absolute inset-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((src, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: i === active ? 1 : 0 }}
        >
          <img
            src={src}
            alt=""
            className="w-full h-full object-cover"
            fetchPriority={i === 0 ? "high" : "auto"}
            loading={i === 0 ? "eager" : "lazy"}
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
      ))}

      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center border border-white/60 text-white hover:bg-white/20 transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center border border-white/60 text-white hover:bg-white/20 transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className="w-8 h-8 flex items-center justify-center"
                aria-label={`Go to slide ${i + 1}`}
              >
                <span className={`block rounded-full transition-all duration-300 ${i === active ? 'w-4 h-2 bg-white' : 'w-2 h-2 bg-white/50 hover:bg-white/80'}`} />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [customerCount, setCustomerCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [heroSlides, setHeroSlides] = useState([]);
  const [heroTaglines, setHeroTaglines] = useState([
    "Bold • Loud • Unapologetic",
    "Modern Statement Wear",
    "Graphic Fashion with Attitude",
    "Designed to Be Seen",
    "Wear the Message"
  ]);
  const [heroCta, setHeroCta] = useState("Shop the Vault");
  const [featuredHeading, setFeaturedHeading] = useState("The Hot List");
  const [featuredSub, setFeaturedSub] = useState("Our most-wanted vintage-inspired pieces, curated just for you.");
  const [lollipopBadge, setLollipopBadge] = useState("Featured Brand");
  const [lollipopDesc, setLollipopDesc] = useState("Modern statement wear designed to be bold, loud, and unapologetic.");
  const [lollipopCta, setLollipopCta] = useState("Explore Lollipop");
  const [popsiclebadge, setPopsiclebadge] = useState("Featured Brand");
  const [popsicleDesc, setPopsicleDesc] = useState("Retro gaming vibes and electric street style from the 80s arcade era.");
  const [popsicleCta, setPopsicleCta] = useState("Explore Popsicle");

  // Calculate years since Lollipop was founded (1999)
  const lollipopYears = new Date().getFullYear() - 1999;

  useEffect(() => {
    fetch(`${TSPM_API}/api/site-content`)
      .then(r => r.json())
      .then(data => {
        const slides = [data.pop_hero_img, data.pop_hero_2, data.pop_hero_3].filter(Boolean);
        if (slides.length > 0) setHeroSlides(slides);

        const taglines = [
          data.pop_hero_tagline_1, data.pop_hero_tagline_2, data.pop_hero_tagline_3,
          data.pop_hero_tagline_4, data.pop_hero_tagline_5,
        ].filter(Boolean);
        if (taglines.length > 0) setHeroTaglines(taglines);

        if (data.pop_hero_cta) setHeroCta(data.pop_hero_cta);
        if (data.pop_featured_heading) setFeaturedHeading(data.pop_featured_heading);
        if (data.pop_featured_sub) setFeaturedSub(data.pop_featured_sub);
        if (data.pop_lollipop_badge) setLollipopBadge(data.pop_lollipop_badge);
        if (data.pop_lollipop_desc) setLollipopDesc(data.pop_lollipop_desc);
        if (data.pop_lollipop_cta) setLollipopCta(data.pop_lollipop_cta);
        if (data.pop_popsicle_badge) setPopsiclebadge(data.pop_popsicle_badge);
        if (data.pop_popsicle_desc) setPopsicleDesc(data.pop_popsicle_desc);
        if (data.pop_popsicle_cta) setPopsicleCta(data.pop_popsicle_cta);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadData = async () => {
      // Defer non-critical Swell requests until after first paint to prioritize LCP (Hero)
      // Increasing deferral from 100ms to 500ms to reduce main-thread TBT during initial render
      setTimeout(async () => {
        try {
          // Initialize data (seeding is critical for some mock environments)
          await seedAPI.seed();

          const [featuredResponse, stats] = await Promise.all([
            productsAPI.getFeatured(),
            statsAPI.getStats()
          ]);
          setFeaturedProducts(featuredResponse.data);
          setTotalProducts(stats.products || 0);
          setCustomerCount(stats.customers || 0);
        } catch (error) {
          console.error('Failed to load home page data:', error);
        } finally {
          setLoading(false);
        }
      }, 500);
    };
    loadData();
  }, []);

  return (
    <div className="grain relative">
      {/* Hero Section - Clean & Centered */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gray-100">
        {heroSlides.length > 0 && <HeroSlider slides={heroSlides} />}
        {/* Rotating Tagline */}
        <div className="relative z-10 text-center">
          <Suspense fallback={<Skeleton type="text" />}>
            <RotatingTagline taglines={heroTaglines} />
          </Suspense>
          <div className="mt-8 flex flex-col items-center gap-6">
            <Magnetic>
              <Link
                to="/shop"
                className="btn-lollipop px-8 py-4 text-lg font-bold uppercase tracking-widest sticker-shadow inline-flex items-center gap-2 group"
                data-cursor="SHOP"
              >
                {heroCta}
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Magnetic>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-4 py-24 sm:px-6 lg:px-8">
        <div className="flex justify-between items-end mb-12">
          <div>
            <h2 className="font-lollipop text-4xl sm:text-5xl font-bold mb-4">{featuredHeading}</h2>
            <p className="font-body text-gray-600 max-w-md">{featuredSub}</p>
          </div>
          <Magnetic>
            <Link to="/shop" className="font-body font-bold uppercase tracking-wider hover:underline flex items-center gap-2" data-cursor="MORE">
              View All <ArrowRight size={16} />
            </Link>
          </Magnetic>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="aspect-square w-full" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-6 w-1/4" />
              </div>
            ))
          ) : (
            featuredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-black text-white py-24 overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 text-center">
            <div>
              <div className="font-popsicle text-4xl sm:text-6xl font-bold mb-2 text-[#00FFFF]">{lollipopYears}</div>
              <div className="font-body text-sm uppercase tracking-widest text-gray-400">Years in Style</div>
            </div>
            <div>
              <div className="font-popsicle text-4xl sm:text-6xl font-bold mb-2 text-[#FFC1CC]">{totalProducts}</div>
              <div className="font-body text-sm uppercase tracking-widest text-gray-400">Unique Designs</div>
            </div>
            <div>
              <div className="font-popsicle text-4xl sm:text-6xl font-bold mb-2 text-[#98FF98]">2</div>
              <div className="font-body text-sm uppercase tracking-widest text-gray-400">Iconic Brands</div>
            </div>
            <div>
              <div className="font-popsicle text-4xl sm:text-6xl font-bold mb-2 text-[#FFF44F]">{customerCount}+</div>
              <div className="font-body text-sm uppercase tracking-widest text-gray-400">Happy Customers</div>
            </div>
          </div>
        </div>
        {/* Decorative background text */}
        <div className="absolute top-1/2 left-0 -translate-y-1/2 font-black text-[20vw] leading-none opacity-5 whitespace-nowrap pointer-events-none select-none italic">
          HOUSE OF LOLLIPOP × POPSICLE ARCADE × VINTAGE VAULT
        </div>
      </section>

      {/* Brand Teasers */}
      <section className="py-24 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              whileHover={{ y: -10 }}
              className="bg-[#FFC1CC] p-12 border-2 border-black sticker-shadow relative overflow-hidden group"
            >
              <div className="relative z-10">
                <span className="brand-badge bg-white mb-6">{lollipopBadge}</span>
                <h3 className="font-lollipop text-4xl font-bold mb-4">Lollipop</h3>
                <p className="font-body text-lg mb-8 max-w-sm">{lollipopDesc}</p>
                <Magnetic>
                  <Link to="/shop?brand=lollipop" className="btn-popsicle px-8 py-3 inline-block" data-cursor="BOLD">{lollipopCta}</Link>
                </Magnetic>
              </div>
              <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-white/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
            </motion.div>

            <motion.div
              whileHover={{ y: -10 }}
              className="bg-[#00FFFF] p-12 border-2 border-black sticker-shadow relative overflow-hidden group"
            >
              <div className="relative z-10">
                <span className="brand-badge bg-white mb-6">{popsiclebadge}</span>
                <h3 className="font-popsicle text-4xl font-bold mb-4">Popsicle</h3>
                <p className="font-body text-lg mb-8 max-w-sm">{popsicleDesc}</p>
                <Magnetic>
                  <Link to="/shop?brand=popsicle" className="btn-lollipop px-8 py-3 inline-block" data-cursor="RETRO">{popsicleCta}</Link>
                </Magnetic>
              </div>
              <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-white/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
